// Which lessons a rubric standard is scored on. Frontend mirror of
// ObservationStandard.applies_to (rubrics/models/standard.py) and of
// rubrics.services.authoring._scope_covers — keep them in sync.
//
// A standard carries `subject_scope` ("all" | "only" | "except") and
// `subjects`: [{uuid, name}] on API rows, a uuid array in the template
// editor's state. Both shapes are accepted.

export const SUBJECT_SCOPES = ["all", "only", "except"];

const uuidsOf = (subjects) => (subjects || []).map((s) => s?.uuid ?? s);

/** A standard with no scope (or "all") is scored on every lesson. */
export const isUnscoped = (scope) => !scope || scope === "all";

/** Is a lesson in `subjectUuid` (null = no subject) scored on this scope?
 *  A lesson with no subject sits outside every "only" list and inside every
 *  "except" one. */
export const scopeAppliesTo = (scope, subjects, subjectUuid) => {
  if (isUnscoped(scope)) return true;
  const listed = subjectUuid != null && uuidsOf(subjects).includes(subjectUuid);
  return scope === "only" ? listed : !listed;
};

export const standardAppliesTo = (standard, subjectUuid) =>
  scopeAppliesTo(standard?.subject_scope, standard?.subjects, subjectUuid);

/** May a booking in `subjectUuid` keep `standard` as a focus? One with no
 *  subject yet keeps every standard: it may simply not have named it.
 *  Client-side only; the server's _focus_for_lesson checks just the rubric. */
export const bookingAllows = (standard, subjectUuid) =>
  !subjectUuid || standardAppliesTo(standard, subjectUuid);

/** Does a lesson's subject decide which standards it is scored on? Then the
 *  observation form must ask for it — the server refuses the submit without. */
export const rubricNeedsSubject = (standards) =>
  (standards || []).some((s) => !isUnscoped(s.subject_scope));

/** Is every lesson scored on `inner` scored on `outer` too? Each side is
 *  [scope, subjects]. "all" and "except" reach subjects no list names (and
 *  lessons with no subject), so only an "except" list can contain one. */
export const scopeCovers = ([outerScope, outerSubjects], [innerScope, innerSubjects]) => {
  if (isUnscoped(outerScope)) return true;
  const outer = new Set(uuidsOf(outerSubjects));
  const inner = uuidsOf(innerSubjects);
  if (innerScope === "only") {
    return outerScope === "only"
      ? inner.every((u) => outer.has(u))
      : inner.every((u) => !outer.has(u));
  }
  return outerScope === "except" && innerScope === "except"
    && [...outer].every((u) => inner.includes(u));
};

/** "Only: Physics, Chemistry" / "Except: PE" — empty for an unscoped standard. */
export const formatSubjectScope = (t, scope, names) =>
  isUnscoped(scope) || !names.length
    ? ""
    : t(`subjectScope.${scope}Short`, { subjects: names.join(", ") });

/** The scope label of a standard in the template editor's state, whose
 *  subjects are uuids named through the subject catalog. */
export const editorScopeLabel = (t, standard, nameOf) =>
  formatSubjectScope(t, standard.subjectScope, standard.subjects.map(nameOf));

/** The scope label of an API standard row, which carries its subjects' names. */
export const standardScopeLabel = (t, standard) =>
  formatSubjectScope(
    t,
    standard?.subject_scope,
    (standard?.subjects || []).map((s) => s.name),
  );

/** Does a standard's history take this lesson (any payload carrying its
 *  `domain_subject`)? Exactly standardAppliesTo: a lesson with no subject
 *  counts toward "all" and "except" standards, never toward "only" ones. */
export const appliesToLesson = (standard, lesson) =>
  standardAppliesTo(standard, lesson?.domain_subject?.uuid ?? null);

/** The frontend StandardLens (observations/services/standard_lens.py — keep
 *  in sync): reads a lesson's score row as one of today's `standards`. A row
 *  counts toward the standard of the same LINEAGE — so an earlier version's
 *  lessons carry over — and only while that standard applies to the lesson's
 *  subject. Returns `(lineage, lesson) => standard | null`. */
export const makeStandardLens = (standards) => {
  const byLineage = new Map((standards || []).map((s) => [s.lineage, s]));
  return (lineage, lesson) => {
    const standard = byLineage.get(lineage);
    return standard && appliesToLesson(standard, lesson) ? standard : null;
  };
};

/** Has this rubric (`standards`) a standard of `lineage` that does not apply
 *  to a lesson in `subjectUuid` (null = no subject)? A lineage it does not
 *  know is not ruled out: nothing says that row was out of scope
 *  (StandardLens.answers). */
export const rulesOut = (standards, lineage, subjectUuid) =>
  (standards || []).some(
    (s) => s.lineage === lineage && !standardAppliesTo(s, subjectUuid),
  );

/** Why a lesson (an observation detail) does or does not count toward today's
 *  `standard`, held by rubric version `versionUuid`: "scored"; "outOfScope",
 *  the standard does not apply to it; or it has no score row of the
 *  standard's lineage — "otherRubric" when it was scored on another version,
 *  "notScored" on this one (the standard did not apply when it was
 *  submitted). Its score rows are the record — present even when the viewer
 *  may not see the numbers — so a lesson that is not scored is never "0%". */
export const lessonStatusOn = (detail, standard, versionUuid) => {
  if (!appliesToLesson(standard, detail)) return "outOfScope";
  if ((detail?.standard_scores || []).some((s) => s.standard_lineage === standard.lineage)) {
    return "scored";
  }
  return detail?.template_version?.uuid === versionUuid ? "notScored" : "otherRubric";
};

/** Does this lesson (an observation detail) count toward today's `standard`? */
const lessonScoredOn = (detail, standard) =>
  lessonStatusOn(detail, standard) === "scored";

const NOT_SCORED_TITLES = {
  outOfScope: "subjectScope.notScoredHere",
  otherRubric: "teacherModal.otherRubric",
  notScored: "teacherModal.notScoredOnStandard",
};

/** Is this a lessonStatusOn status that leaves the lesson unscored? */
export const isNotScored = (status) => Object.keys(NOT_SCORED_TITLES).includes(status);

/** The tooltip of a matrix cell whose lesson is not scored on the standard —
 *  a dashed «–», distinct from «not observed». */
export const notScoredTitle = (t, status) => t(NOT_SCORED_TITLES[status]);

/** The answer a lesson gave to today's `question`, matched by lineage so a
 *  lesson scored on an earlier version still answers it. */
export const answerTo = (detail, question) =>
  (detail?.answers || []).find((a) => a.question_lineage === question.lineage);

/** Did any of these lessons, counting toward `standard`, answer one of its
 *  `questions`? False means there is nothing yet to put in its matrix. */
export const anyAnswerTo = (details, standard, questions) =>
  details.some(
    (detail) =>
      lessonScoredOn(detail, standard) && questions.some((q) => answerTo(detail, q)),
  );

/** The standards worth a card on one teacher's page: every shared one, plus a
 *  subject-scoped one that reaches a subject they teach or that they already
 *  have a score on. A PE-only card on a maths teacher's page would sit at
 *  "no data" forever. */
export const standardsForTeacher = (standards, subjectUuids, scores) =>
  standards.filter(
    (std) =>
      isUnscoped(std.subject_scope) ||
      scores?.[std.uuid] != null ||
      subjectUuids.some((uuid) => standardAppliesTo(std, uuid)),
  );
